import { regionByCode } from "../../lexicon/regions.js";
import { QUALITY, type Quality } from "../../scales.js";
import { familyOfGrape } from "../families.js";
import {
  markerBand,
  type MarkerBand,
  type Verdict,
  criterionPoints,
} from "../../marker/scoring.js";

/**
 * What one wine of a Level 4 paper is worth, out of 100. The table is pinned here and nowhere
 * else: the device and the edge both read it. The written argument is the one part a marker reads;
 * the other ninety come from fields and chips, so they are exact and explainable.
 */
export const L4_WINE_MARKS = {
  grape: 16,
  country: 6,
  region: 10,
  vintageBand: 8,
  quality: 10,
  commercial: 6,
  qualityCriteria: 9,
  ageing: 10,
  ageingCriteria: 15,
  argument: 10,
} as const;

export type L4Field = keyof typeof L4_WINE_MARKS;
export const L4_FIELDS = [
  "grape",
  "country",
  "region",
  "vintageBand",
  "quality",
  "commercial",
  "qualityCriteria",
  "ageing",
  "ageingCriteria",
  "argument",
] as const satisfies readonly L4Field[];

export const L4_SECTIONS = ["identification", "quality", "ageing", "argument"] as const;
export type L4Section = (typeof L4_SECTIONS)[number];

const SECTION_OF: Readonly<Record<L4Field, L4Section>> = {
  grape: "identification",
  country: "identification",
  region: "identification",
  vintageBand: "identification",
  quality: "quality",
  commercial: "quality",
  qualityCriteria: "quality",
  ageing: "ageing",
  ageingCriteria: "ageing",
  argument: "argument",
};

/** The ladders, lowest first; a call one step from the key earns half, anything further nothing. */
/** The note's own quality scale: one ladder for the paper, the badge and calibration. */
export const L4_QUALITY = QUALITY;
export const L4_COMMERCIAL = ["entry", "mid", "premium"] as const;
export const L4_AGEING = ["now", "medium", "long"] as const;
export type L4Quality = Quality;
export type L4Commercial = (typeof L4_COMMERCIAL)[number];
export type L4Ageing = (typeof L4_AGEING)[number];

const BAND_RANGE = /^b-(\d{4})-(\d{4})$/;
const BAND_ORIGIN = 2010;
const BAND_WIDTH = 3;
const BAND_FIRST = BAND_ORIGIN - 6 * BAND_WIDTH;
const BAND_LAST = BAND_ORIGIN + 9 * BAND_WIDTH - 1;

/** Three-year bands from 1992 so a paper can hold an older vintage; `undefined` outside them. */
export function vintageBandOf(year: number): string | undefined {
  if (!Number.isInteger(year) || year < BAND_FIRST || year > BAND_LAST) return undefined;
  const start = year - ((((year - BAND_ORIGIN) % BAND_WIDTH) + BAND_WIDTH) % BAND_WIDTH);
  return `b-${String(start)}-${String(start + BAND_WIDTH - 1)}`;
}

function bandStart(band: string): number | undefined {
  const match = BAND_RANGE.exec(band);
  if (match === null) return undefined;
  const start = Number(match[1]);
  return vintageBandOf(start) === band ? start : undefined;
}

export const isVintageBand = (value: unknown): value is string =>
  typeof value === "string" && bandStart(value) !== undefined;

/** The lexicon's knowledge, passed in so this file stays free of the grape and region tables. */
export interface L4Relations {
  readonly grape: (called: string, key: string) => "exact" | "family" | "none";
  readonly region: (called: string, key: string) => "exact" | "related" | "none";
}

/** What a learner called for one wine. Every part is optional: an empty field earns nothing. */
export interface L4WineCall {
  readonly grape?: string;
  readonly country?: string;
  readonly region?: string;
  readonly vintageBand?: string;
  readonly quality?: L4Quality;
  readonly commercial?: L4Commercial;
  readonly ageing?: L4Ageing;
  readonly qualityChips?: readonly string[];
  readonly ageingChips?: readonly string[];
  /** One verdict per criterion of the written argument, from the marker or the checklist; none without text. */
  readonly argument?: readonly Verdict[] | undefined;
}

/** The answer behind a wine; an absent identification part is not judged. */
export interface L4WineKey {
  readonly grape?: string;
  readonly country?: string;
  readonly region?: string;
  readonly vintageBand?: string;
  readonly quality: L4Quality;
  readonly commercial: L4Commercial;
  readonly ageing: L4Ageing;
  readonly qualityCriteria: readonly string[];
  readonly ageingCriteria: readonly string[];
}

/** A host's reveals take the place of the archetype key where the host gave one. */
export function keyWithFallback(
  host: Partial<L4WineKey> | undefined,
  archetype: L4WineKey,
): L4WineKey {
  const picked = Object.fromEntries(
    Object.entries(host ?? {}).filter(([, value]) => value !== undefined),
  );
  return { ...archetype, ...picked };
}

export interface L4FieldMark {
  readonly earned: number;
  /** Zero when the key leaves the field open, so it is out of the total. */
  readonly max: number;
}

export interface L4SectionMark extends L4FieldMark {
  /** Undefined when the section has no marks to earn. */
  readonly band?: MarkerBand;
}

export interface L4Loss {
  readonly field: L4Field;
  /** The guide entry that explains it, in our words, in the curriculum. */
  readonly guide: L4Field;
  readonly earned: number;
  readonly max: number;
}

export interface L4WineMark {
  readonly fields: Readonly<Record<L4Field, L4FieldMark>>;
  readonly sections: Readonly<Record<L4Section, L4SectionMark>>;
  readonly points: number;
  readonly possible: number;
  readonly score: number;
  readonly losses: readonly L4Loss[];
}

/**
 * How many steps apart two rungs of a ladder are, or undefined when either is not on it. Calibration's
 * delta is the same difference of positions, so a badge's "one step" and a meter's "on target" share a unit.
 */
export function ladderDistance(
  ladder: readonly string[],
  a: string,
  b: string,
): number | undefined {
  const x = ladder.indexOf(a);
  const y = ladder.indexOf(b);
  return x < 0 || y < 0 ? undefined : Math.abs(x - y);
}

/** The lexicon's knowledge: a grape in the key's family is near, a region of the same country is related. */
export const defaultL4Relations: L4Relations = {
  grape: (called, key) => {
    if (called === key) return "exact";
    const family = familyOfGrape(called);
    return family !== undefined && family === familyOfGrape(key) ? "family" : "none";
  },
  region: (called, key) => {
    if (called === key) return "exact";
    const a = regionByCode(called);
    const b = regionByCode(key);
    return a !== undefined && b !== undefined && a.country === b.country ? "related" : "none";
  },
};

const rungs = <T extends string>(ladder: readonly T[], called: T | undefined, key: T): number => {
  const at = called === undefined ? -1 : ladder.indexOf(called);
  if (at < 0) return 0;
  const gap = Math.abs(at - ladder.indexOf(key));
  return gap === 0 ? 1 : gap === 1 ? 0.5 : 0;
};

const share = (max: number, part: number, whole: number): number =>
  whole === 0 ? 0 : Math.round((max * part) / whole);

const chipsEarned = (max: number, chips: readonly string[] | undefined, key: readonly string[]) => {
  const wanted = new Set(key);
  const hit = new Set((chips ?? []).filter((chip) => wanted.has(chip)));
  return share(max, hit.size, wanted.size);
};

const bandOf = (earned: number, max: number): MarkerBand | undefined =>
  max <= 0 ? undefined : markerBand(Math.round((100 * earned) / max));

const sectionOf = (earned: number, max: number): L4SectionMark => {
  const band = bandOf(earned, max);
  return { earned, max, ...(band === undefined ? {} : { band }) };
};

/** One wine, marked field by field. Pure, total and the same on the device and on the edge. */
export function markL4Wine(call: L4WineCall, key: L4WineKey, relations: L4Relations): L4WineMark {
  const M = L4_WINE_MARKS;
  const grapeKind =
    key.grape === undefined || call.grape === undefined
      ? "none"
      : relations.grape(call.grape, key.grape);
  const regionKind =
    key.region === undefined || call.region === undefined
      ? "none"
      : relations.region(call.region, key.region);
  const verdicts = call.argument ?? [];
  const argumentPoints = verdicts.reduce((sum, verdict) => sum + criterionPoints(verdict, 1), 0);

  const earned: Record<L4Field, number> = {
    grape: grapeKind === "exact" ? M.grape : grapeKind === "family" ? M.grape / 2 : 0,
    country: key.country !== undefined && call.country === key.country ? M.country : 0,
    region: regionKind === "exact" ? M.region : regionKind === "related" ? M.region / 2 : 0,
    vintageBand: bandsEarned(call.vintageBand, key.vintageBand),
    quality: M.quality * rungs(L4_QUALITY, call.quality, key.quality),
    commercial: M.commercial * rungs(L4_COMMERCIAL, call.commercial, key.commercial),
    qualityCriteria: chipsEarned(M.qualityCriteria, call.qualityChips, key.qualityCriteria),
    ageing: M.ageing * rungs(L4_AGEING, call.ageing, key.ageing),
    ageingCriteria: chipsEarned(M.ageingCriteria, call.ageingChips, key.ageingCriteria),
    argument: share(M.argument, argumentPoints, verdicts.length),
  };
  const open: Record<L4Field, boolean> = {
    grape: key.grape === undefined,
    country: key.country === undefined,
    region: key.region === undefined,
    vintageBand: key.vintageBand === undefined,
    quality: false,
    commercial: false,
    qualityCriteria: key.qualityCriteria.length === 0,
    ageing: false,
    ageingCriteria: key.ageingCriteria.length === 0,
    argument: false,
  };

  const fieldMark = (field: L4Field): L4FieldMark => ({
    earned: open[field] ? 0 : earned[field],
    max: open[field] ? 0 : M[field],
  });
  const fields: Record<L4Field, L4FieldMark> = {
    grape: fieldMark("grape"),
    country: fieldMark("country"),
    region: fieldMark("region"),
    vintageBand: fieldMark("vintageBand"),
    quality: fieldMark("quality"),
    commercial: fieldMark("commercial"),
    qualityCriteria: fieldMark("qualityCriteria"),
    ageing: fieldMark("ageing"),
    ageingCriteria: fieldMark("ageingCriteria"),
    argument: fieldMark("argument"),
  };
  const sectionMark = (section: L4Section): L4SectionMark => {
    const own = L4_FIELDS.filter((field) => SECTION_OF[field] === section);
    return sectionOf(
      own.reduce((sum, field) => sum + fields[field].earned, 0),
      own.reduce((sum, field) => sum + fields[field].max, 0),
    );
  };
  const sums: Record<L4Section, L4SectionMark> = {
    identification: sectionMark("identification"),
    quality: sectionMark("quality"),
    ageing: sectionMark("ageing"),
    argument: sectionMark("argument"),
  };

  const points = L4_FIELDS.reduce((sum, field) => sum + fields[field].earned, 0);
  const possible = L4_FIELDS.reduce((sum, field) => sum + fields[field].max, 0);
  return {
    fields,
    sections: sums,
    points,
    possible,
    score: possible === 0 ? 0 : Math.round((100 * points) / possible),
    losses: L4_FIELDS.flatMap((field) =>
      fields[field].max > 0 && fields[field].earned < fields[field].max
        ? [{ field, guide: field, ...fields[field] }]
        : [],
    ),
  };
}

function bandsEarned(called: string | undefined, key: string | undefined): number {
  if (called === undefined || key === undefined) return 0;
  const a = bandStart(called);
  const b = bandStart(key);
  if (a === undefined || b === undefined) return 0;
  const gap = Math.abs(a - b);
  return gap === 0
    ? L4_WINE_MARKS.vintageBand
    : gap === BAND_WIDTH
      ? L4_WINE_MARKS.vintageBand / 2
      : 0;
}

// ---- a paper -----------------------------------------------------------------------

export type L4IssueKind = "duplicate-wine" | "empty-call" | "unknown-value" | "unknown-field";

export interface L4Issue {
  readonly kind: L4IssueKind;
  readonly wineId: string;
  readonly field?: string;
}

export interface L4SittingWine {
  readonly wineId: string;
  /** What storage holds; anything not a well-formed call is cleaned and named in the issues. */
  readonly call: unknown;
  readonly key: L4WineKey;
}

/** How much of the paper was sat: a practice paper is two halves on separate evenings. */
export interface L4Halves {
  readonly sat: number;
  readonly of: number;
}

export interface L4PaperMark {
  readonly wines: readonly (L4WineMark & { readonly wineId: string })[];
  readonly score: number;
  /** Undefined for a half-sat paper: its band is not read off twelve wines. */
  readonly band?: MarkerBand;
  readonly sections: Readonly<Record<L4Section, L4SectionMark>>;
  readonly sectionsMarked: number;
  readonly halves: L4Halves;
  readonly issues: readonly L4Issue[];
}

const CALL_FIELDS: ReadonlySet<string> = new Set([
  "grape",
  "country",
  "region",
  "vintageBand",
  "quality",
  "commercial",
  "ageing",
  "qualityChips",
  "ageingChips",
  "argument",
]);

const VERDICT_VALUES: ReadonlySet<unknown> = new Set(["met", "partly", "not_yet"]);
const isSlug = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0 && value.length <= 64;
const isCountry = (value: unknown): value is string =>
  typeof value === "string" && /^[A-Z]{2}$/.test(value);
const isStrings = (value: unknown): value is string[] =>
  Array.isArray(value) && value.length <= 40 && value.every(isSlug);
const isVerdicts = (value: unknown): value is Verdict[] =>
  Array.isArray(value) && value.length <= 16 && value.every((v) => VERDICT_VALUES.has(v));
const isOf =
  <T extends string>(ladder: readonly T[]) =>
  (value: unknown): value is T =>
    typeof value === "string" && ladder.some((rung) => rung === value);

/** Keeps what is well formed of a stored call and names what is not; it never throws. */
function cleanCall(wineId: string, raw: unknown, issues: L4Issue[]): L4WineCall {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    issues.push({ kind: "empty-call", wineId });
    return {};
  }
  const fields = new Map(Object.entries(raw));
  for (const field of fields.keys()) {
    if (!CALL_FIELDS.has(field)) issues.push({ kind: "unknown-field", wineId, field });
  }
  const take = <T>(field: string, guard: (value: unknown) => value is T): T | undefined => {
    const value = fields.get(field);
    if (value === undefined || value === null) return undefined;
    if (guard(value)) return value;
    issues.push({ kind: "unknown-value", wineId, field });
    return undefined;
  };
  const grape = take("grape", isSlug);
  const country = take("country", isCountry);
  const region = take("region", isSlug);
  const vintageBand = take("vintageBand", isVintageBand);
  const quality = take("quality", isOf(L4_QUALITY));
  const commercial = take("commercial", isOf(L4_COMMERCIAL));
  const ageing = take("ageing", isOf(L4_AGEING));
  const qualityChips = take("qualityChips", isStrings);
  const ageingChips = take("ageingChips", isStrings);
  const argument = take("argument", isVerdicts);
  return {
    ...(grape === undefined ? {} : { grape }),
    ...(country === undefined ? {} : { country }),
    ...(region === undefined ? {} : { region }),
    ...(vintageBand === undefined ? {} : { vintageBand }),
    ...(quality === undefined ? {} : { quality }),
    ...(commercial === undefined ? {} : { commercial }),
    ...(ageing === undefined ? {} : { ageing }),
    ...(qualityChips === undefined ? {} : { qualityChips }),
    ...(ageingChips === undefined ? {} : { ageingChips }),
    ...(argument === undefined ? {} : { argument }),
  };
}

const has = (list: readonly unknown[] | undefined): boolean => (list?.length ?? 0) > 0;

function sectionsAnswered(call: L4WineCall): ReadonlySet<L4Section> {
  const answered = new Set<L4Section>();
  if ([call.grape, call.country, call.region, call.vintageBand].some((v) => v !== undefined)) {
    answered.add("identification");
  }
  if (call.quality !== undefined || call.commercial !== undefined || has(call.qualityChips)) {
    answered.add("quality");
  }
  if (call.ageing !== undefined || has(call.ageingChips)) answered.add("ageing");
  if (has(call.argument)) answered.add("argument");
  return answered;
}

/**
 * A paper is the rounded mean of its wines. A repeated wine id keeps the first, a bad value is
 * ignored and named, and a half-sat paper reports what was sat without a band of its own.
 */
export function markL4Paper(
  sitting: readonly L4SittingWine[],
  relations: L4Relations,
  halves: L4Halves = { sat: 1, of: 1 },
): L4PaperMark {
  const issues: L4Issue[] = [];
  const seen = new Set<string>();
  const marked: (L4WineMark & { wineId: string })[] = [];
  const answered = new Set<L4Section>();
  for (const wine of sitting) {
    if (seen.has(wine.wineId)) {
      issues.push({ kind: "duplicate-wine", wineId: wine.wineId });
      continue;
    }
    seen.add(wine.wineId);
    const call = cleanCall(wine.wineId, wine.call, issues);
    for (const section of sectionsAnswered(call)) answered.add(section);
    marked.push({ wineId: wine.wineId, ...markL4Wine(call, wine.key, relations) });
  }
  const score =
    marked.length === 0
      ? 0
      : Math.round(marked.reduce((sum, wine) => sum + wine.score, 0) / marked.length);
  const total = (section: L4Section): L4SectionMark =>
    sectionOf(
      marked.reduce((sum, wine) => sum + wine.sections[section].earned, 0),
      marked.reduce((sum, wine) => sum + wine.sections[section].max, 0),
    );
  const sections: Record<L4Section, L4SectionMark> = {
    identification: total("identification"),
    quality: total("quality"),
    ageing: total("ageing"),
    argument: total("argument"),
  };
  const whole = halves.sat >= halves.of && marked.length > 0;
  return {
    wines: marked,
    score,
    ...(whole ? { band: markerBand(score) } : {}),
    sections,
    sectionsMarked: answered.size,
    halves,
    issues,
  };
}
